import { Howl } from 'howler';
import { getRandomSound } from './sound';

type ResultSoundContext = {
	streak: number;
	pitch: number;
};

type PlayResultSoundOptions = {
	context?: string;
	winPitchIncrement?: number;
	minPitch?: number;
	maxPitch?: number;
	resetOnLoss?: boolean;
	winIntensity?: number;
};

type PlayResultSoundParams = {
	isWin: boolean;
	winSounds: Howl[];
	loseSounds: Howl[];
	options?: PlayResultSoundOptions;
};

const DEFAULT_OPTIONS = {
	context: 'global',
	winPitchIncrement: 0.06,
	minPitch: 1,
	maxPitch: 1.45,
	resetOnLoss: true,
} satisfies Required<Omit<PlayResultSoundOptions, 'winIntensity'>>;

const contextState = new Map<string, ResultSoundContext>();

const getState = (context: string, minPitch: number): ResultSoundContext => {
	const existing = contextState.get(context);
	if (existing) {
		return existing;
	}
	const initial: ResultSoundContext = {
		streak: 0,
		pitch: minPitch,
	};
	contextState.set(context, initial);
	return initial;
};

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);
const clamp01 = (value: number) => clamp(value, 0, 1);

const pickIntensityIndex = (length: number, intensity: number) => {
	if (length <= 1) return 0;
	const safe = clamp01(intensity);
	const idx = Math.floor(safe * length);
	return Math.min(length - 1, Math.max(0, idx));
};

/**
 * Play a win or lose sound with dynamic pitch when winning consecutively.
 * Plinko remains unaffected since it manages its own sounds separately.
 */
export function playResultSound({ isWin, winSounds, loseSounds, options }: PlayResultSoundParams) {
	const { context, winPitchIncrement, minPitch, maxPitch, resetOnLoss, winIntensity } = {
		...DEFAULT_OPTIONS,
		...(options ?? {}),
	};
	const state = getState(context, minPitch);

	if (isWin) {
		state.streak += 1;
		state.pitch = clamp(minPitch + state.streak * winPitchIncrement, minPitch, maxPitch);
		if (winSounds.length) {
			const shouldUseIntensity = Number.isFinite(winIntensity);
			const sound =
				shouldUseIntensity && winIntensity != null ? winSounds[pickIntensityIndex(winSounds.length, winIntensity)]! : getRandomSound(winSounds);
			const id = sound.play();
			sound.rate(state.pitch, id);
		}
		return;
	}

	if (resetOnLoss) {
		state.streak = 0;
		state.pitch = minPitch;
	}

	if (loseSounds.length) {
		const sound = getRandomSound(loseSounds);
		const id = sound.play();
		sound.rate(minPitch, id);
	}
}
